import { checkNoChosenYears } from 'portfolio3/features/pdf/utils/checkNoChosenYears';
import { checkSectionEmptiness } from 'portfolio3/features/pdf/utils/checkSectionEmptiness';
import { sortPeriods } from 'portfolio3/features/pdf/utils/sortPeriods';
import { performanceModel } from 'portfolio3/features/performance';
import { createSelector } from 'reselect';
import { chosenYearsSelector } from 'selectors/drawers';
import { academicYearsTouchedSelector } from 'selectors/drawers/linkSharingDrawer';
import { getCurrentLearningYear } from 'utils';

import { finalPerfomanceYearsSelector } from './finalPerfomanceYearsSelector';
import { independentDiagnosticsYearsSelector } from './independentDiagnosticsYearsSelector';
import { personalDiagnosticsYearsSelector } from './personalDiagnosticsYearsSelector';
import { selfDiagnosticsYearsSelector } from './selfDiagnosticsYearsSelector';
import { studyDocumentsSelectorYearsSelector } from './studyDocumentsSelectorYearsSelector';
import {
  studyEgeYearsSelector,
  studyGiaListYearsSelector,
  studyGve9YearsSelector,
  studyGve11YearsSelector,
  studyOgeYearsSelector,
  studyOtherGiaYearsSelector,
  studyTrialExamsYearsSelector,
} from './studyGiaListYearsSelector';
import { studyOlympiadsYearsSelector } from './studyOlympiadsYearsSelector';
import { studySpoPracticeYearsSelector } from './studySpoPracticeYearsSelector';
import { studySpoTrainingYearsSelector } from './studySpoTrainingYearsSelector';

export const studySectionAcademicYearsDataSelector = createSelector(
  [
    // Данные раздела Учёба
    studySpoTrainingYearsSelector,
    studySpoPracticeYearsSelector,
    studyDocumentsSelectorYearsSelector,
    finalPerfomanceYearsSelector,
    independentDiagnosticsYearsSelector,
    selfDiagnosticsYearsSelector,
    personalDiagnosticsYearsSelector,
    studyGiaListYearsSelector,
    studyOgeYearsSelector,
    studyEgeYearsSelector,
    studyGve9YearsSelector,
    studyGve11YearsSelector,
    studyOtherGiaYearsSelector,
    studyOlympiadsYearsSelector,
    studyTrialExamsYearsSelector,
    // Выбранные года
    chosenYearsSelector,
    academicYearsTouchedSelector,
    // Данные по средним годам (досутпны только для текущего года)
    performanceModel.selectors.learningYearAveragePerformanceSelector,
  ],
  (
    studySpoTrainingYears,
    studySpoPracticeYears,
    documentsYears,
    finalPerfomanceYears,
    independentDiagnosticsYears,
    selfDiagnosticsYears,
    personalDiagnosticsYears,
    studyGiaListYears,
    studyOgeYears,
    studyEgeYears,
    studyGve9Years,
    studyGve11Years,
    studyOtherGiaYearsSelector,
    studyOlympiadsYears,
    studyTrialExamsYears,
    chosenYears,
    academicYearsTouched,
    learningYearAveragePerformance,
  ) => {
    const currentLearningYear = getCurrentLearningYear();

    const hasAveragePerformance =
      learningYearAveragePerformance.content.subjectsPerformance.length > 0 &&
      // Проверка нужно только если пользователь выбирал года самостоятельно, иначе считается что выбраны все
      (chosenYears.includes(currentLearningYear) || !academicYearsTouched);

    const emptyDataMap = {
      studies: true,
      // Сведения об обучении
      trainingInfo: checkNoChosenYears(studySpoTrainingYears, chosenYears, academicYearsTouched),
      // Практика
      practice: checkNoChosenYears(studySpoPracticeYears, chosenYears, academicYearsTouched),
      // Документы
      documents: checkNoChosenYears(documentsYears, chosenYears, academicYearsTouched),
      // Предметные результаты, только finalPerfomance
      performance:
        checkNoChosenYears(finalPerfomanceYears, chosenYears, academicYearsTouched) && !hasAveragePerformance,

      // Государственная итоговая аттестация
      // ГИА родительский раздел, но его дочерние разделы фильтруют его данные
      gia: checkNoChosenYears(studyGiaListYears, chosenYears, academicYearsTouched),
      // Государственная итоговая аттестация - Основной государственный экзамен
      oge: checkNoChosenYears(studyOgeYears, chosenYears, academicYearsTouched),
      // Пробный основной государственный экзамен (ОГЭ)
      approbationOge: checkNoChosenYears(studyOgeYears, chosenYears, academicYearsTouched),
      // Государственная итоговая аттестация - Единый государственный экзамен
      ege: checkNoChosenYears(studyEgeYears, chosenYears, academicYearsTouched),
      // Государственная итоговая аттестация - Государственный выпускной экзамен (ГВЭ-9)
      gve9: checkNoChosenYears(studyGve9Years, chosenYears, academicYearsTouched),
      // Государственная итоговая аттестация - Государственный выпускной экзамен (ГВЭ-11)
      gve11: checkNoChosenYears(studyGve11Years, chosenYears, academicYearsTouched),
      // Государственная итоговая аттестация - Другое
      other: checkNoChosenYears(studyOtherGiaYearsSelector, chosenYears, academicYearsTouched),

      // Диагностики
      diagnostic: true,
      independentDiagnostic: checkNoChosenYears(independentDiagnosticsYears, chosenYears, academicYearsTouched),
      personalDiagnostic: checkNoChosenYears(personalDiagnosticsYears, chosenYears, academicYearsTouched),
      selfDiagnostic: checkNoChosenYears(selfDiagnosticsYears, chosenYears, academicYearsTouched),

      // Олимпиады
      olympiads: checkNoChosenYears(studyOlympiadsYears, chosenYears, academicYearsTouched),
      // Пробные экзамены
      trialExam: checkNoChosenYears(studyTrialExamsYears, chosenYears, academicYearsTouched),
    };

    // Родительский раздел пуст, когда пусты все его дочерние разделы
    // Диагностики
    emptyDataMap.diagnostic =
      emptyDataMap.independentDiagnostic && emptyDataMap.personalDiagnostic && emptyDataMap.selfDiagnostic;

    const periods: Set<string> = new Set([
      // Учеба
      ...studySpoTrainingYears,
      ...studySpoPracticeYears,
      // Документы возможно можно убрать
      ...documentsYears,
      // // // Для average perfomance данные только для чекущего года, поэтому берем только final
      ...finalPerfomanceYears,
      ...studyGiaListYears,
      ...independentDiagnosticsYears,
      // // // TODO: Разобраться с самодиагностиками
      ...selfDiagnosticsYears,
      ...personalDiagnosticsYears,
      ...studyOlympiadsYears,
    ]);

    return {
      academicYears: sortPeriods(periods),
      emptyDataMap: {
        ...emptyDataMap,
        studies: checkSectionEmptiness(emptyDataMap),
      },
    };
  },
);
